import { useCallback, useState } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';

import {
    type LeadDetailsOverlayKey,
    type LeadDetailsOverlaySearch,
    leadDetailsSearchSchema,
} from '~/features/leadDetails/schemas/leadDetailsSearch.schema';
import { updateLeadDetailsOverlaySearch } from '~/features/leadDetails/utils/normalizeLeadDetailsSearch';

type LeadDetailsOverlayStateOptions<Key extends LeadDetailsOverlayKey> = {
    isRouteControlled: boolean;
    key: Key;
    value: NonNullable<LeadDetailsOverlaySearch[Key]>;
};

export function useLeadDetailsOverlayState<Key extends LeadDetailsOverlayKey>({
    isRouteControlled,
    key,
    value,
}: LeadDetailsOverlayStateOptions<Key>) {
    const [isLocallyOpen, setIsLocallyOpen] = useState(false);
    const navigate = useNavigate();
    const search = leadDetailsSearchSchema.parse(useSearch({ strict: false }));

    const setOpen = useCallback(
        (open: boolean) => {
            if (!isRouteControlled) {
                setIsLocallyOpen(open);
                return;
            }

            navigate({
                replace: !open,
                search: (previous) => updateLeadDetailsOverlaySearch(previous, key, open ? value : undefined),
                to: '.',
            });
        },
        [isRouteControlled, key, navigate, value],
    );

    return {
        isOpen: isRouteControlled ? search[key] === value : isLocallyOpen,
        isRouteControlled,
        setOpen,
    };
}
